import { ApplicationRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval } from 'rxjs';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly appRef = inject(ApplicationRef, { optional: true });
  private readonly ngZone = inject(NgZone);
  private readonly updateAvailable = signal(false);

  readonly isUpdateAvailable = this.updateAvailable.asReadonly();

  constructor() {
    if (!this.swUpdate?.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailable.set(true);
      }
    });

    // Estado irrecuperable → reload silencioso sin intervención del usuario
    this.swUpdate.unrecoverable.subscribe(() => {
      location.reload();
    });

    if (!this.appRef) {
      return;
    }

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));

    // Chequeo al stabilize + cada 5 min
    concat(appIsStable$, interval(CHECK_INTERVAL_MS)).subscribe(() => {
      void this.swUpdate?.checkForUpdate();
    });

    // Chequeo al volver a la pestaña (fuera de zona para no afectar CD)
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.ngZone.run(() => void this.swUpdate?.checkForUpdate());
        }
      });
    });
  }

  dismissUpdate(): void {
    this.updateAvailable.set(false);
  }

  async activateUpdate(): Promise<void> {
    if (!this.swUpdate?.isEnabled) {
      return;
    }
    await this.swUpdate.activateUpdate();
    location.reload();
  }
}
